'use client';

import { ScrollableRow } from '../../../components/scrollable-row';
import { DateSelectorSkeleton } from '../skeleton';
import { formatDateForDisplay } from '../utils/format';

// ─── Date Card ───────────────────────────────────────────────────────────

export interface DateCardProps {
  date: Date;
  isSelected?: boolean;
  /** Compact mode uses smaller sizing (for popups / chat bubbles). */
  compact?: boolean;
  onClick?: (date: Date) => void;
}

export function DateCard({
  date,
  isSelected = false,
  compact = false,
  onClick,
}: DateCardProps) {
  const { dayOfWeek, dayNumber, month } = formatDateForDisplay(date);

  return (
    <button
      type="button"
      onClick={() => onClick?.(date)}
      className={`flex flex-shrink-0 flex-col items-center justify-center text-center transition-colors ${
        compact
          ? 'gap-0 rounded-md px-2 py-1'
          : 'gap-1 rounded-[10px] px-5 py-2'
      } ${
        isSelected
          ? 'force-light:bg-gray-900 force-light:text-white bg-gray-900 text-white dark:bg-white dark:text-gray-900'
          : 'force-light:border-0 force-light:bg-gray-100 force-light:text-gray-900 force-light:hover:bg-gray-200 bg-gray-100 text-gray-900 hover:bg-gray-200 dark:border dark:border-gray-700 dark:bg-gray-800 dark:text-white dark:hover:bg-gray-700'
      }`}
      style={compact ? undefined : { height: 74 }}
    >
      <p
        className={`leading-tight font-light ${compact ? 'text-[9px]' : 'text-sm'} ${
          isSelected
            ? 'force-light:text-gray-300 text-gray-300 dark:text-gray-600'
            : 'force-light:text-gray-400 text-gray-400 dark:text-gray-500'
        }`}
      >
        {dayOfWeek}
      </p>
      <p
        className={`leading-tight font-medium ${compact ? 'text-[11px]' : 'text-base'}`}
      >
        {dayNumber}
      </p>
      <p
        className={`leading-tight font-light ${compact ? 'text-[9px]' : 'text-sm'} ${
          isSelected
            ? 'force-light:text-gray-300 text-gray-300 dark:text-gray-600'
            : 'force-light:text-gray-400 text-gray-400 dark:text-gray-500'
        }`}
      >
        {month}
      </p>
    </button>
  );
}

// ─── Date Selector ───────────────────────────────────────────────────────

export interface DateSelectorProps {
  dates: Date[];
  selectedDate: Date | null;
  isLoading?: boolean;
  isLoadingMore?: boolean;
  /** Compact mode for popups — shows fewer skeleton items and smaller cards. */
  compact?: boolean;
  /** Label shown above the date row. Defaults to "Select Date and Time". */
  label?: string;
  /** Called when a date is selected. */
  onDateSelect?: (date: Date) => void;
  /** Called when the user scrolls to the end (for pagination). */
  onScrollEnd?: () => void;
}

export function DateSelector({
  dates,
  selectedDate,
  isLoading = false,
  isLoadingMore = false,
  compact = false,
  label = 'Select Date & Time',
  onDateSelect,
  onScrollEnd,
}: DateSelectorProps) {
  if (isLoading) {
    return <DateSelectorSkeleton compact={compact} />;
  }

  if (dates.length === 0) {
    return (
      <p className="force-light:text-gray-400 py-4 text-center text-sm text-gray-400 dark:text-gray-500">
        No available dates. Please check back later.
      </p>
    );
  }

  return (
    <ScrollableRow
      label={label}
      showArrows
      compact={compact}
      onScrollEnd={onScrollEnd}
    >
      {dates.map(date => (
        <DateCard
          key={date.toISOString()}
          date={date}
          isSelected={selectedDate?.getTime() === date.getTime()}
          compact={compact}
          onClick={onDateSelect}
        />
      ))}
      {isLoadingMore && (
        <div
          className={`flex flex-shrink-0 items-center ${compact ? 'h-[40px] px-1' : 'h-[74px] px-2'}`}
        >
          <div
            className={`animate-spin rounded-full border-2 border-gray-300 border-t-gray-600 ${compact ? 'h-3 w-3' : 'h-4 w-4'}`}
          />
        </div>
      )}
    </ScrollableRow>
  );
}
